import React from 'react';
import { CheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Player, PlayerWithLeagueContext } from '../../generated-api';
import PlayerAvatar from '../common/PlayerAvatar';

interface PlayerBubbleProps {
  player: Player | PlayerWithLeagueContext;
  isSelected: boolean;
  isLocked?: boolean; // Can't be deselected (e.g. current user)
  teamColor?: string; // e.g. 'red', 'blue'
  onToggle: (playerId: string) => void;
}

// Hardcoded ring classes so Tailwind can detect them at build time
const TEAM_RING_MAP: Record<string, string> = {
  red: 'ring-red-400',
  blue: 'ring-primary-400',
  yellow: 'ring-yellow-400',
  black: 'ring-stone-400',
  pink: 'ring-pink-400',
  purple: 'ring-purple-400',
  white: 'ring-stone-300',
  green: 'ring-green-400',
};

const PlayerBubble: React.FC<PlayerBubbleProps> = ({
  player,
  isSelected,
  isLocked,
  teamColor,
  onToggle,
}) => {
  const ringClass = teamColor ? TEAM_RING_MAP[teamColor] || 'ring-primary-500' : 'ring-primary-500';

  return (
    <button
      data-track="wizard_player_bubble"
      type="button"
      onClick={() => !isLocked && onToggle(player.id)}
      className={`flex flex-col items-center gap-0.5 w-[72px]
                 transition-all duration-150
                 ${isLocked ? 'cursor-default' : 'active:scale-[0.95] motion-reduce:active:scale-100'}`}
      aria-pressed={isSelected}
      aria-label={isLocked ? `${player.name} (you)` : `${isSelected ? 'Deselect' : 'Select'} ${player.name}`}
    >
      <div className={`relative rounded-full transition-all duration-150
                       ${isSelected ? `ring-[3px] ring-offset-2 ${ringClass}` : ''}`}>
        <PlayerAvatar name={player.name} imageUrl={player.imageUrl} size="wizard" />

        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5
                          bg-primary-500 rounded-full
                          flex items-center justify-center
                          border-2 border-[var(--bg-base)]">
            <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} />
          </div>
        )}
      </div>

      {/* Player name */}
      <span className={`text-xs leading-tight text-center line-clamp-1 w-full
                        ${isSelected ? 'text-heading font-medium' : 'text-muted'}`}>
        {player.name}
      </span>

      {/* "You" badge for locked player */}
      {isLocked && (
        <span className="text-[10px] font-semibold text-primary-500 uppercase tracking-wide">
          You
        </span>
      )}
    </button>
  );
};

// "New Player" bubble variant
export const NewPlayerBubble: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    data-track="wizard_new_player"
    type="button"
    onClick={onClick}
    className="flex flex-col items-center gap-0.5 w-[72px]
               transition-all duration-150 active:scale-[0.95]
               motion-reduce:active:scale-100"
    aria-label="Add new player"
    data-testid="new-player-button"
  >
    <div className="w-14 h-14 rounded-full border-2 border-dashed border-edge-input
                    flex items-center justify-center
                    hover:border-primary-400 hover:bg-primary-50
                    dark:hover:bg-primary-900/20
                    transition-colors">
      <PlusIcon className="w-6 h-6 text-disabled" />
    </div>
    <span className="text-xs leading-tight text-center text-muted w-full">
      New
    </span>
  </button>
);

export default PlayerBubble;
